import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import * as en from '../data/portfolio';
import * as fr from '../data/portfolio.fr';
import { strings } from '../data/strings';

/**
 * FR/EN language switching. `t` holds UI strings (data/strings.js) and
 * `data` the localized portfolio content (data/portfolio[.fr].js).
 * Defaults to the browser language on first visit, then persists the choice.
 */
const LanguageContext = createContext({
  lang: 'en',
  toggle: () => {},
  t: strings.en,
  data: en,
});

const getInitial = () => {
  try {
    // ?lang= URL param wins — lets Simon share e.g. simonsaifi.vercel.app/?lang=fr
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl === 'fr' || fromUrl === 'en') return fromUrl;
    const saved = localStorage.getItem('lang');
    if (saved === 'fr' || saved === 'en') return saved;
  } catch (e) { /* ignore storage errors */ }
  if (typeof navigator !== 'undefined' && (navigator.language || '').toLowerCase().startsWith('fr')) {
    return 'fr';
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitial);

  useEffect(() => {
    try {
      localStorage.setItem('lang', lang);
    } catch (e) { /* ignore storage errors */ }
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const toggle = useCallback(() => {
    setLang((l) => (l === 'en' ? 'fr' : 'en'));
  }, []);

  const value = useMemo(
    () => ({ lang, toggle, t: strings[lang], data: lang === 'fr' ? fr : en }),
    [lang, toggle]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useI18n = () => useContext(LanguageContext);

export default LanguageContext;
